import { Component, Input, OnChanges } from '@angular/core';
import { LabelI18nService } from 'src/app/core/_services/label-i18n.service';
import * as Highcharts from 'highcharts';
import HC_exporting from 'highcharts/modules/exporting';
import HC_offlineExporting from 'highcharts/modules/offline-exporting';
import { Indicator } from 'src/app/core/_models/indicator';
import { ProjectEcoPro } from 'src/app/core/_models/projectEcoPro';
import { Structure } from 'src/app/core/_models/structure';
import { StructureType } from 'src/app/core/_models/structureType';
import { HIGHCHARTS_EXPORTING, HIGHCHARTS_TRANSLATIONS } from 'src/constants/graph.constants';

HC_exporting(Highcharts);
HC_offlineExporting(Highcharts);

let componentContext: any;

@Component({
  selector: 'app-bar-indicators-global-byStructureTypes',
  templateUrl: './bar-indicators-global-byStructureTypes.component.html',
  styleUrls: ['./bar-indicators-global-byStructureTypes.component.scss']
})
export class BarIndicatorsGlobalByStructureTypesComponent implements OnChanges {
  @Input() projectData: ProjectEcoPro = {};
  @Input() indicators: Indicator[] = [];
  structures: Structure[] = [];

  indicatorComparatif: Indicator = {
    isChecked: false,
    displayName: ''
  };

  updateFlag: boolean = false;
  totalRef: number = 0;

  // Highcharts config

  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {
    chart: {
      type: 'column'
    },
    lang: HIGHCHARTS_TRANSLATIONS,
    title: {
      text: "Comparaison par scénario et catégorie d'aménagement"
    },
    exporting: HIGHCHARTS_EXPORTING,
    legend: {
      align: 'right',
      x: -30,
      verticalAlign: 'top',
      y: 0,
      floating: false,
      backgroundColor: Highcharts.defaultOptions.legend!.backgroundColor || 'white',
      borderColor: '#CCC',
      borderWidth: 1,
      shadow: false
    },
    tooltip: {
      headerFormat: '<b>{point.x}</b><br/>',
      pointFormat: '{series.name}: {point.y}<br/>Total: {point.stackTotal}'
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        dataLabels: {
          enabled: true
        }
      }
    }
  };

  constructor(public labelI18n: LabelI18nService) {
    componentContext = this;
  }

  ngOnChanges() {
    if (this.indicatorComparatif) this.formatDataForGraph();
  }

  selectIndicatorChange(indicatorId: any) {
    let i: Indicator | undefined = this.indicators?.find((indicator: Indicator) => indicator.indicatorId == indicatorId);
    if (i) {
      this.indicatorComparatif = i;
      this.formatDataForGraph();
    }
  }

  formatDataForGraph() {
    let series: { type: string; name: string | undefined; data: number[]; color: string | undefined }[] = [];
    let structureTypes: StructureType[] = [];
    let structuresCategoriesNames: string[] = [];

    this.projectData.solutionsEcoPro?.forEach((solution) => {
      solution.structureTypesProject.forEach((structureTypeProject) => {
        if (structureTypeProject.structureType.displayName && !structuresCategoriesNames.includes(structureTypeProject.structureType.displayName)) {
          structuresCategoriesNames.push(structureTypeProject.structureType.displayName);
        }
      });
    });

    this.projectData.solutionsEcoPro?.forEach((solution) => {
      structuresCategoriesNames.forEach((structureCategoryName) => {
        // Si la solution n'a pas de valeur pour la catégorie de structure.

        if (solution.structureTypesProject.find((structureTypeProject) => structureTypeProject.structureType.displayName == structureCategoryName) == null) {
          let structureTypeRef = structureTypes.find((structureType) => structureType.displayName == structureCategoryName);

          if (structureTypeRef == null) {
            structureTypes.push({ displayName: structureCategoryName, dataForGraph: [0] });
          } else {
            structureTypeRef.dataForGraph?.push(0);
          }
        }
      });

      solution.structureTypesProject.forEach((structureTypeProject) => {
        const ceuValueForStructureType = +structureTypeProject.impactsCEU?.find((impact) => impact.displayName == this.indicatorComparatif.displayName)?.value!.toFixed(2)!;

        let structureTypeRef = structureTypes.find((structureType) => structureType.displayName == structureTypeProject.structureType.displayName);

        if (structureTypeRef == null) {
          structureTypes.push({ displayName: structureTypeProject.structureType.displayName, dataForGraph: [ceuValueForStructureType], color: structureTypeProject.structureType.color });
        } else {
          structureTypeRef.dataForGraph?.push(ceuValueForStructureType);
        }
      });
    });

    structureTypes.forEach((structureType) => {
      if (structureType.dataForGraph) {
        series.push({
          type: 'column',
          name: structureType.displayName,
          data: structureType.dataForGraph,
          color: structureType.color
        });
      }
    });

    this.chartOptions.xAxis = {
      categories: this.projectData.solutionsEcoPro?.map((solution) => solution.displayName!)
    };

    this.chartOptions.yAxis = {
      min: 0,
      title: {
        text: componentContext?.labelI18n.indicatorLabelWithUnit(componentContext?.indicatorComparatif)
      },
      stackLabels: {
        enabled: true,
        useHTML: true,
        y: -10,
        style: {
          fontWeight: 'bold',
          fontSize: '15px',
          color:
            // theme
            (Highcharts.defaultOptions.title!.style && Highcharts.defaultOptions.title!.style.color) || 'black'
        },
        formatter: function () {
          if (this.x == 0) {
            componentContext.totalRef = this.total;
            return '';
          } else {
            if (componentContext.totalRef) {
              const percentageDiff = (100 * (this.total - componentContext.totalRef)) / componentContext.totalRef;
              if (!isNaN(percentageDiff) && this.total != 0) {
                const displayValue = percentageDiff == 0 ? '&nbsp' + '-' + '&nbsp' : percentageDiff > 0 ? '+' + Math.round(percentageDiff) + '%' : Math.round(percentageDiff) + '%';
                const backgroundColor = percentageDiff < 0 ? '#25e043' : percentageDiff > 0 ? '#ff4c4c' : 'lightgray';

                return '<span style="background-color:' + backgroundColor + '; font-size: 13px; padding: 3px; border:1px solid">' + displayValue + '</span>';
              } else {
                return '';
              }
            } else {
              return '';
            }
          }
        }
      }
    };
    this.chartOptions.series = series as Highcharts.SeriesOptionsType[];
    this.updateFlag = true;
  }
}
